import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DashboardLayout from '../components/DashboardLayout';
import { Plus, Bot, MoreVertical, Trash2, Settings, MessageSquare } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { agentsAPI } from '../lib/api';

const AgentsList = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setError(null);
      setLoading(true);
      const data = await agentsAPI.list();
      // Ensure data is an array - handle both array and object responses
      const agentsArray = Array.isArray(data) ? data : (data?.agents || data?.items || []);
      setAgents(agentsArray);
    } catch (error) {
      console.error('Error loading agents:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to load agents. Please check your backend configuration.');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgent.name.trim()) return;
    
    setCreating(true);
    setCreateError(null);
    
    try {
      // Check if backend URL is configured
      if (!process.env.REACT_APP_BACKEND_URL) {
        throw new Error('Backend URL is not configured. Please set REACT_APP_BACKEND_URL in Netlify environment variables.');
      }
      
      const agent = await agentsAPI.create(newAgent);
      
      // Validate agent response before navigation
      if (!agent || !agent.agent_id) {
        throw new Error('Invalid agent response: missing agent_id');
      }
      
      setAgents([agent, ...agents]);
      setCreateDialogOpen(false);
      setNewAgent({ name: '', description: '' });
      setCreateError(null);
      
      // Small delay to ensure state updates before navigation
      setTimeout(() => {
        navigate(`/dashboard/agents/${agent.agent_id}`);
      }, 100);
    } catch (error) {
      
      console.error('Error creating agent:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create agent. Please check your backend configuration.';
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await agentsAPI.delete(agentId);
      setAgents(agents.filter(a => a.agent_id !== agentId));
    } catch (error) {
      console.error('Error deleting agent:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Agents</h1>
            <p className="text-slate-400 mt-1">Create and manage your AI agents</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="h-4 w-4 mr-2" />
                New Agent
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Agent</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Give your agent a name and description to get started.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-200">Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Customer Support Bot"
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-slate-200">Description (optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="What will this agent help with?"
                    value={newAgent.description}
                    onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white resize-none"
                    rows={3}
                  />
                </div>
              </div>
              {createError && (
                <div className="px-6 py-3 bg-red-900/20 border border-red-800/50 rounded-lg mx-6">
                  <p className="text-sm text-red-400">{createError}</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => {
                  setCreateDialogOpen(false);
                  setCreateError(null);
                  setNewAgent({ name: '', description: '' });
                }} className="text-slate-400">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateAgent} 
                  disabled={!newAgent.name.trim() || creating}
                  className="bg-indigo-600 hover:bg-indigo-500"
                >
                  {creating ? 'Creating...' : 'Create Agent'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="bg-red-900/20 border-red-800/50">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Agents</h3>
                  <p className="text-red-300 text-sm mb-4">{error}</p>
                  {!process.env.REACT_APP_BACKEND_URL && (
                    <p className="text-red-200 text-xs mb-4">
                      <strong>Configuration Issue:</strong> REACT_APP_BACKEND_URL is not set in Netlify environment variables.
                    </p>
                  )}
                  <Button onClick={loadAgents} variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/20">
                    Retry
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agents Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-slate-900/50 border-slate-800 animate-pulse">
                <CardHeader className="space-y-2">
                  <div className="h-6 bg-slate-800 rounded w-3/4"></div>
                  <div className="h-4 bg-slate-800 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-slate-800 rounded w-full"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : !Array.isArray(agents) || agents.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                <Bot className="h-8 w-8 text-slate-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No agents yet</h3>
              <p className="text-slate-400 text-center mb-4 max-w-sm">
                Create your first AI agent to start building intelligent chatbots for your website.
              </p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Agent
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(agents) && agents.map((agent) => (
              <Card 
                key={agent.agent_id} 
                className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer group"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${agent.widget_settings?.primary_color || '#6366f1'}20` }}
                      >
                        <Bot 
                          className="h-5 w-5" 
                          style={{ color: agent.widget_settings?.primary_color || '#6366f1' }}
                        />
                      </div>
                      <div>
                        <CardTitle className="text-white text-base">{agent.name}</CardTitle>
                        <Badge 
                          variant="outline" 
                          className={`mt-1 ${agent.is_active ? 'border-emerald-500/50 text-emerald-400' : 'border-slate-600 text-slate-400'}`}
                        >
                          {agent.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                        <DropdownMenuItem 
                          className="text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer"
                          onClick={() => navigate(`/dashboard/agents/${agent.agent_id}`)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-400 hover:text-red-300 hover:bg-slate-800 cursor-pointer"
                          onClick={() => handleDeleteAgent(agent.agent_id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-400 line-clamp-2 mb-4">
                    {agent.description || 'No description'}
                  </CardDescription>
                  <Link 
                    to={`/dashboard/agents/${agent.agent_id}`}
                    className="inline-flex items-center text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Configure Agent
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AgentsList;
